import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Compact Edge Score strip for order windows.
 * Shows overall 0-10 score + decision badge + gradient bar.
 */
export default function SentimentStrip({ symbol, colors }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!symbol) return;
    setLoading(true);
    const clean = symbol.replace('.NS', '');
    axios.get(`${API_URL}/stocks/${clean}/edge-score`)
      .then(res => {
        if (res.data?.overall_score != null) setData(res.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [symbol]);

  if (loading) {
    return (
      <div className="app-card rounded-lg px-3 py-2 animate-pulse" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderWidth: '1px', borderColor: colors.border }}>
        <div className="h-3 w-24 rounded" style={{ backgroundColor: colors.inputBg }} />
        <div className="h-2 w-full rounded-full mt-2" style={{ backgroundColor: colors.inputBg }} />
      </div>
    );
  }

  if (!data) return null;

  const score = data.overall_score;
  const markerPct = (score / 10) * 100;
  const scoreColor = score >= 7 ? '#16C784' : score >= 4 ? '#f59e0b' : '#ef4444';
  const label = score >= 7 ? 'Strong' : score >= 4 ? 'Moderate' : 'Weak';
  const action = data.decision?.action;

  const badgeColors = {
    'Bullish Setup':  { bg: `${colors.profit}20`, text: colors.profit },
    'Moderate Setup': { bg: `${colors.profit}15`, text: colors.profit },
    'Strong Setup':   { bg: '#7c3aed20',          text: '#7c3aed' },
    'Neutral':        { bg: `${colors.accent}20`, text: colors.accent },
    'Weak Setup':     { bg: `${colors.loss}20`,   text: colors.loss },
  };
  const bc = action ? (badgeColors[action] || badgeColors['Neutral']) : null;

  return (
    <div className="app-card rounded-lg px-3 py-2.5" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderWidth: '1px', borderColor: colors.border }}>
      {/* Header row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Shield className="w-3.5 h-3.5" style={{ color: colors.accent }} />
          <span className="text-[14px] font-semibold" style={{ color: colors.primaryText }}>
            Edge Score
          </span>
          {action && bc && (
            <span className="text-[12px] px-1.5 py-px rounded font-medium"
              style={{ backgroundColor: bc.bg, color: bc.text }}>
              {action}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <span className="text-lg font-bold" style={{ color: scoreColor }}>
            {score.toFixed(1)}
          </span>
          <span className="text-[13px] font-medium" style={{ color: '#64748b' }}>/10</span>
        </div>
      </div>

      {/* Gradient bar */}
      <div className="relative w-full h-2 rounded-full overflow-hidden"
        style={{ background: 'linear-gradient(to right, #ef4444, #f59e0b, #16C784)' }}>
        <div className="absolute top-0 h-full w-0.5 bg-white shadow-sm" style={{ left: `${markerPct}%`, transform: 'translateX(-50%)' }} />
      </div>

      {/* Label row */}
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[12px]" style={{ color: '#64748b' }}>Bearish</span>
        <span className="text-[13px] font-medium" style={{ color: scoreColor }}>
          {label}
        </span>
        <span className="text-[12px]" style={{ color: '#64748b' }}>Bullish</span>
      </div>
    </div>
  );
}
